/** App analytics is paused until the external property's automatic collection
 * has been reviewed. Saved preferences remain withdrawable. The separate
 * marketing site's settings are outside this application.
 */
export const MEASUREMENT_ID = "G-1C2GRG611Y";
export const APP_ANALYTICS_ENABLED = false;
const STORAGE_KEY = "mavinty_analytics_consent";
export const CONSENT_EVENT = "mavinty-analytics-preference";
type Consent = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    "ga-disable-G-1C2GRG611Y"?: boolean;
  }
}
export function storedConsent(): Consent | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch { return null; }
}
function persist(value: Consent) {
  try { localStorage.setItem(STORAGE_KEY, value); } catch { /* Remain disabled. */ }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}
function stopCollection() {
  window["ga-disable-G-1C2GRG611Y"] = true;
  document.getElementById("ga4-loader")?.remove();
  // Do not enqueue denied-consent commands: an already-loaded tag can send a
  // cookieless consent ping. The documented disable flag stops this property.
  if (window.dataLayer) window.dataLayer.length = 0;
}
function removeAppCookies() {
  const names = document.cookie.split(";").map(c => c.split("=")[0].trim())
    .filter(name => name === "_ga" || name === "_ga_1C2GRG611Y");
  const host = window.location.hostname;
  const domains = ["", host, "." + host];
  if (host === "mavinty.com" || host.endsWith(".mavinty.com")) domains.push("mavinty.com", ".mavinty.com");
  for (const name of names) for (const domain of domains) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ""}`;
  }
}
export function grantAnalytics(): void {
  // No new consent request is offered during the pause.
  if (!APP_ANALYTICS_ENABLED) return;
  persist("granted");
}
export function denyAnalytics(): void {
  stopCollection();
  persist("denied");
  removeAppCookies();
}
export function restoreConsent(): void {
  stopCollection();
  if (storedConsent() === "denied") removeAppCookies();
}
export function trackPageView(_path: string): void {
  // Intentionally no third-party request or event queue while paused.
  stopCollection();
}

/** Future reviewed analytics may use this small route allowlist only. Never
 * return dates, identifiers, queries, hashes or health/chat route names.
 */
export function maskIds(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0];
  return ["/login", "/register", "/pricing"].includes(pathname) ? pathname : "/app";
}
if (typeof window !== "undefined") {
  restoreConsent();
  window.addEventListener("storage", event => {
    if (event.key === STORAGE_KEY || event.key === null) {
      restoreConsent();
      window.dispatchEvent(new Event(CONSENT_EVENT));
    }
  });
}
